import React, { useState, useEffect } from "react";
import {
  Card,
  List,
  Avatar,
  Spin,
  Typography,
  Space,
  Tag,
  message,
  Row,
  Col,
  Switch,
  Button,
} from "antd";
import { UserOutlined, HomeOutlined, EyeOutlined } from "@ant-design/icons";
import { useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchRoles,
  fetchUserDetail,
  addRoleToUser,
  removeRoleFromUser,
} from "../store/adminApi/adminThunk";
import { Breadcrumb } from "react-bootstrap";

const { Title, Text } = Typography;

const UserDetailPage = () => {
  const { userId } = useParams();
  const dispatch = useDispatch();

  const { roles = [], status: rolesStatus } = useSelector(
    (state) => state.adminApi || {}
  );
  const { userDetail, status: userStatus } = useSelector(
    (state) => state.adminApi || {}
  );

  const [loadingRoles, setLoadingRoles] = useState({});

  useEffect(() => {
    dispatch(fetchUserDetail(userId));
    if (!roles.length) dispatch(fetchRoles());
  }, [dispatch, userId]);

  const handleRoleToggle = async (roleId, checked) => {
    if (!userDetail) return;

    setLoadingRoles((prev) => ({ ...prev, [roleId]: true }));

    try {
      if (checked) {
        await dispatch(addRoleToUser({ userId: userDetail.id, roleId })).unwrap();
        message.success("Rol muvaffaqiyatli qo‘shildi");
      } else {
        await dispatch(removeRoleFromUser({ userId: userDetail.id, roleId })).unwrap();
        message.success("Rol muvaffaqiyatli olib tashlandi");
      }
      // yangilash uchun userni qaytadan chaqiramiz
      dispatch(fetchUserDetail(userId));
    } catch (error) {
      message.error(error || "Xatolik yuz berdi");
    } finally {
      setLoadingRoles((prev) => ({ ...prev, [roleId]: false }));
    }
  };

  const isUserHasRole = (roleId) =>
    userDetail?.roles?.some((r) => r.id === roleId);

  const renderUserInfo = () => {
    if (userStatus === "loading" || !userDetail) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    const { first_name, surname, patronymic, picture, departments = [] } =
      userDetail;

    const capitalize = (str) => {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    return (
      <div direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Avatar
            size={100}
            src={picture}
            icon={<UserOutlined />}
            style={{ marginBottom: 16 }}
          />
          <Title level={4}>
            {capitalize(surname)} {capitalize(first_name)} {capitalize(patronymic)}
          </Title>
          {/* <Tag color="blue">ID: {userDetail.id}</Tag> */}
        </div>

        {departments.length > 0 && (
          <>
            <Title level={5}>Bo‘lim va lavozimlar</Title>
            <Space direction="vertical" style={{ width: "100%" }}>
              {departments.map((dept, index) => (
                <Card
                  key={dept.id || index}
                  size="small"
                  bordered
                  style={{ background: "#fafafa" }}
                >
                  <p>
                    <Text strong>Bo‘lim:</Text> {dept.department_name?.name || "-"}
                  </p>
                  <p>
                    <Text strong>Lavozim:</Text> {dept.staff_position?.name || "-"}
                  </p>
                  <p>
                    <Text strong>Stavka:</Text> {dept.employment_staff?.name || "-"}
                  </p>
                  <p>
                    <Text strong>Holati:</Text>{" "}
                    <Tag
                      color={
                        dept.employee_status?.name === "Ishlamoqda"
                          ? "green"
                          : "red"
                      }
                    >
                      {dept.employee_status?.name || "Noma'lum"}
                    </Tag>
                  </p>
                </Card>
              ))}
            </Space>
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/users" }}>
          Xodimlar ro'yxati
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {userDetail
            ? `${userDetail.surname} ${userDetail.first_name}`
            : "Yuklanmoqda..."}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row gutter={[24, 24]}>
        {/* Chap tomon */}
        <Col xs={24} lg={10} xl={8}>
          <Card title="Xodim ma'lumotlari">{renderUserInfo()}</Card>

          {/* Admin Actions Card */}
          <Card title="Admin amallar" style={{ marginTop: 24 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Link to={`/admin/users/${userId}/tasks`}>
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  block
                  size="large"
                >
                  Foydalanuvchi tasklarini ko'rish
                </Button>
              </Link>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Bu foydalanuvchining barcha tasklari (yaratgan va tayinlangan)
              </Text>
            </Space>
          </Card>
        </Col>

        {/* O'ng tomon */}
        <Col xs={24} lg={14} xl={16}>
          <Card title="Foydalanuvchi rollari">
            <List
              dataSource={roles}
              loading={rolesStatus === "loading"}
              renderItem={(role) => {
                return (
                  <List.Item
                    style={{
                      borderBottom: "1px solid #f0f0f0",
                      padding: "12px 8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>{role.name}</Text>
                      <div style={{ color: "#888" }}>{role.code}</div>
                    </div>
                    <Switch
                      checked={isUserHasRole(role.id)}
                      loading={loadingRoles[role.id]}
                      onChange={(checked) => handleRoleToggle(role.id, checked)}
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserDetailPage;
